import {
	Clock01Icon,
	CoinsSwapIcon,
	Invoice02Icon,
	Store01Icon,
	Tag01Icon,
	TimeScheduleIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback } from "react";
import { BranchSelector } from "@/components/branch-selector";
import { PageContent } from "@/components/layout/page-content";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { Spinner } from "@/components/ui/spinner";
import { BusinessHoursCard } from "@/features/branch/components/business-hours-card";
import { SettingsCard } from "@/features/branch/components/settings-card";
import { StatCard } from "@/features/branch/components/stat-card";
import { DAYS } from "@/features/branch/lib/constants";
import { useActiveBranchGuard } from "@/hooks/use-active-branch-guard";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useMe } from "@/hooks/use-me";

interface BranchSearch {
	branch?: string;
}

export const Route = createFileRoute("/(dashboard)/branch/")({
	component: BranchPage,
	validateSearch: (search: Record<string, unknown>): BranchSearch => ({
		branch: search.branch as string | undefined,
	}),
});

function BranchPage() {
	const navigate = useNavigate();
	const { branch: searchBranchId } = useSearch({
		from: "/(dashboard)/branch/",
	});

	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	useActiveBranchGuard({ orgId, branchId });

	const branchData =
		branchesData === undefined
			? undefined
			: (branchesData.find((b) => b._id === branchId) ?? null);

	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			if (typeof value !== "string") return;
			navigate({ to: ".", search: { branch: value } });
		},
		[navigate],
	);

	const goToSettings = useCallback(
		(
			to:
				| "/branch/settings"
				| "/branch/settings/hours"
				| "/branch/settings/tax",
		) => {
			navigate({ to, search: { branch: branchId } });
		},
		[navigate, branchId],
	);

	const controls = (
		<BranchSelector
			branches={branchesData ?? []}
			value={branchId as Id<"branches"> | undefined}
			onValueChange={handleBranchChange}
			placeholder="Select branch"
			className="w-48"
			onViewAll={() => navigate({ to: "/settings/branches" })}
			onCreateNew={() => navigate({ to: "/settings/branches" })}
		/>
	);

	if (!branchId && !isLoadingBranches) {
		return (
			<PageContent title="Branch" controls={controls}>
				<div className="flex flex-1 flex-col justify-end pb-12">
					<EmptyStateWithSkeleton
						title="No branches found"
						description="Create a branch to view its overview."
						actionLabel="Create branch"
						onAction={() => navigate({ to: "/settings/branches" })}
						icon={
							<HugeiconsIcon
								icon={Store01Icon}
								className="h-5 w-5 text-neutral-700"
								strokeWidth={2.2}
							/>
						}
					/>
				</div>
			</PageContent>
		);
	}

	if (!branchData) {
		return (
			<PageContent title="Branch" controls={controls}>
				<div className="flex items-center justify-center py-12">
					<Spinner />
				</div>
			</PageContent>
		);
	}

	return (
		<PageContent title="Branch" controls={controls}>
			<div className="space-y-6">
				<div>
					<h2 className="text-2xl font-bold tracking-tight">
						{branchData.name}
					</h2>
					{branchData.address ? (
						<p className="text-muted-foreground mt-1 text-sm">
							{branchData.address}
						</p>
					) : (
						<p className="text-muted-foreground mt-1 text-sm italic">
							No address set —{" "}
							<button
								type="button"
								className="text-primary hover:underline not-italic"
								onClick={() => goToSettings("/branch/settings")}
							>
								Add one
							</button>
						</p>
					)}
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					<StatCard
						icon={Tag01Icon}
						label="Tax ID"
						value={branchData.taxId ?? "—"}
					/>
					<StatCard
						icon={Invoice02Icon}
						label="Tax Rate"
						value={branchData.taxRate == null ? "—" : `${branchData.taxRate}%`}
					/>
					<StatCard
						icon={CoinsSwapIcon}
						label="Commission"
						value={
							branchData.staffCommissionRate == null
								? "—"
								: `${branchData.staffCommissionRate}%`
						}
					/>
				</div>

				<div className="grid gap-4 sm:grid-cols-2">
					<SettingsCard
						icon={Clock01Icon}
						title="Opening Hours"
						description="Manage weekly schedule and open/close times"
						onClick={() => goToSettings("/branch/settings/hours")}
						summary={
							branchData.businessHours
								? `${DAYS.filter((d) => branchData.businessHours?.[d]?.enabled).length} of 7 days open`
								: "Not configured"
						}
					/>

					<SettingsCard
						icon={TimeScheduleIcon}
						title="Tax Settings"
						description="Configure tax rate and tax ID for this branch"
						onClick={() => goToSettings("/branch/settings/tax")}
						summary={
							branchData.taxRate == null
								? "Not configured"
								: `${branchData.taxRate}% VAT`
						}
					/>
				</div>

				{branchData.businessHours && (
					<BusinessHoursCard
						businessHours={branchData.businessHours}
						onEdit={() => goToSettings("/branch/settings/hours")}
					/>
				)}
			</div>
		</PageContent>
	);
}
